import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';

const normalizedPublicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const pdfWorkerSrc = `${normalizedPublicUrl}/pdf.worker.min.mjs`;

if (pdfjsLib.GlobalWorkerOptions.workerSrc !== pdfWorkerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
}

const getExtension = (fileName = '') => {
    const normalized = fileName.toLowerCase();
    const index = normalized.lastIndexOf('.');
    return index >= 0 ? normalized.slice(index + 1) : '';
};

const getPreviewKind = (file = null) => {
    if (!file) {
        return 'unsupported';
    }

    const fileType = (file.fileType || file.blob?.type || '').toLowerCase();
    const extension = getExtension(file.fileName);

    if (fileType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) {
        return 'image';
    }

    if (fileType.includes('pdf') || extension === 'pdf') {
        return 'pdf';
    }

    if (
        fileType.startsWith('text/') ||
        ['txt', 'csv', 'log', 'json', 'xml'].includes(extension)
    ) {
        return 'text';
    }

    if (fileType.startsWith('video/')) {
        return 'video';
    }

    if (fileType.startsWith('audio/')) {
        return 'audio';
    }

    return 'unsupported';
};

const downloadFromObjectUrl = (file) => {
    if (!file?.url) {
        return;
    }

    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.fileName || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
};

function PdfCanvasPreview({ file }) {
    const { t } = useTranslation();
    const containerRef = React.useRef(null);
    const [status, setStatus] = useState({ isLoading: true, error: '' });

    useEffect(() => {
        let isCancelled = false;
        let loadingTask = null;
        let pdfDocument = null;
        const container = containerRef.current;
        if (!container || !file?.blob) {
            setStatus({ isLoading: false, error: t('filePreviewUnsupported') });
            return undefined;
        }

        container.innerHTML = '';
        setStatus({ isLoading: true, error: '' });

        const renderPdf = async () => {
            try {
                const data = await file.blob.arrayBuffer();
                if (isCancelled) {
                    return;
                }

                loadingTask = pdfjsLib.getDocument({ data });
                pdfDocument = await loadingTask.promise;
                if (isCancelled) {
                    return;
                }

                for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
                    const page = await pdfDocument.getPage(pageNumber);
                    if (isCancelled) {
                        return;
                    }

                    const viewport = page.getViewport({ scale: 1.35 });
                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    const pixelRatio = window.devicePixelRatio || 1;

                    canvas.width = Math.floor(viewport.width * pixelRatio);
                    canvas.height = Math.floor(viewport.height * pixelRatio);
                    canvas.style.width = `${Math.floor(viewport.width)}px`;
                    canvas.style.height = `${Math.floor(viewport.height)}px`;
                    canvas.className = 'max-w-full rounded-2xl bg-white shadow-lg';

                    const pageWrap = document.createElement('div');
                    pageWrap.className = 'mb-5 flex flex-col items-center gap-2 last:mb-0';

                    const pageLabel = document.createElement('div');
                    pageLabel.className = 'rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-600';
                    pageLabel.textContent = t('filePreviewPdfPage', { page: pageNumber, total: pdfDocument.numPages });

                    pageWrap.appendChild(pageLabel);
                    pageWrap.appendChild(canvas);
                    container.appendChild(pageWrap);

                    const renderContext = {
                        canvasContext: context,
                        viewport,
                    };

                    if (pixelRatio !== 1) {
                        renderContext.transform = [pixelRatio, 0, 0, pixelRatio, 0, 0];
                    }

                    await page.render(renderContext).promise;
                }

                if (!isCancelled) {
                    setStatus({ isLoading: false, error: '' });
                }
            } catch (error) {
                if (!isCancelled) {
                    setStatus({ isLoading: false, error: error.message || t('filePreviewPdfFailed') });
                }
            } finally {
                pdfDocument?.destroy?.();
            }
        };

        renderPdf();

        return () => {
            isCancelled = true;
            loadingTask?.destroy?.();
            pdfDocument?.destroy?.();
            container.innerHTML = '';
        };
    }, [file, t]);

    return (
        <div className="min-h-[55vh]">
            {status.isLoading && (
                <div className="flex min-h-[55vh] items-center justify-center text-sm font-medium text-slate-500">
                    {t('loading')}
                </div>
            )}
            {status.error && (
                <div className="flex min-h-[55vh] items-center justify-center rounded-2xl border border-gray-200 bg-white p-6 text-sm font-semibold text-gray-700">
                    {status.error}
                </div>
            )}
            <div ref={containerRef} className={status.isLoading || status.error ? 'hidden' : 'mx-auto flex max-w-full flex-col items-center'} />
        </div>
    );
}

export default function AttachmentPreviewModal({ previewState, onClose }) {
    const { t } = useTranslation();
    const { isOpen, isLoading, error, file } = previewState;
    const [textContent, setTextContent] = useState('');
    const previewKind = useMemo(() => getPreviewKind(file), [file]);

    useEffect(() => {
        let isCancelled = false;
        setTextContent('');

        if (!file?.blob || previewKind !== 'text') {
            return undefined;
        }

        file.blob.text()
            .then((content) => {
                if (!isCancelled) {
                    setTextContent(content);
                }
            })
            .catch(() => {
                if (!isCancelled) {
                    setTextContent(t('filePreviewTextFailed'));
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [file, previewKind, t]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-4">
            <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
                <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                        <h3 className="text-lg font-bold text-slate-900">{t('filePreviewTitle')}</h3>
                        <p className="mt-1 truncate text-sm text-slate-500">{file?.fileName || '---'}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        {file?.url && (
                            <button
                                type="button"
                                onClick={() => downloadFromObjectUrl(file)}
                                className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-700"
                            >
                                {t('downloadAttachment')}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                        >
                            {t('close')}
                        </button>
                    </div>
                </div>

                <div className="min-h-[55vh] flex-1 overflow-auto bg-slate-100 p-4">
                    {isLoading ? (
                        <div className="flex min-h-[55vh] items-center justify-center text-sm font-medium text-slate-500">
                            {t('loading')}
                        </div>
                    ) : error ? (
                        <div className="flex min-h-[55vh] items-center justify-center rounded-2xl border border-red-100 bg-red-50 p-6 text-sm font-semibold text-red-700">
                            {error}
                        </div>
                    ) : previewKind === 'image' ? (
                        <div className="flex min-h-[55vh] items-center justify-center">
                            <img src={file.url} alt={file.fileName} className="max-h-[75vh] max-w-full rounded-2xl bg-white object-contain shadow-lg" />
                        </div>
                    ) : previewKind === 'pdf' ? (
                        <PdfCanvasPreview file={file} />
                    ) : previewKind === 'text' ? (
                        <pre className="min-h-[55vh] whitespace-pre-wrap break-words rounded-2xl border border-slate-200 bg-white p-5 text-sm leading-6 text-slate-800">
                            {textContent || t('loading')}
                        </pre>
                    ) : previewKind === 'video' ? (
                        <video controls src={file.url} className="mx-auto max-h-[75vh] max-w-full rounded-2xl bg-black shadow-lg">
                            {t('filePreviewUnsupported')}
                        </video>
                    ) : previewKind === 'audio' ? (
                        <div className="flex min-h-[55vh] items-center justify-center">
                            <audio controls src={file.url} className="w-full max-w-2xl">
                                {t('filePreviewUnsupported')}
                            </audio>
                        </div>
                    ) : (
                        <div className="flex min-h-[55vh] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
                            <div className="mb-4 rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold tracking-widest text-slate-500">FILE</div>
                            <p className="text-base font-semibold text-slate-800">{t('filePreviewUnsupported')}</p>
                            <p className="mt-2 text-sm text-slate-500">{t('filePreviewDownloadHint')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
